/* card-colors.js — Sneak Freak (AphCom) · <sf-card-colors>
   The colourway strip under a collection/search card's photo. Markup contract: snippets/product-card.liquid
   renders a.card-b__color[href="…?variant=", data-colour, data-colour-handle, data-variant, data-image],
   one of them carrying data-initial + aria-current and .is-selected, inside
   sf-card-colors.card-b__colors[role=toolbar]. .is-selected is the live record of the shopper's pick and is
   what this file reads back when it reconnects; data-initial only says which colourway the card was built
   in, which is the one whose media track can be restored exactly. Loaded by sections/main-collection.liquid
   and sections/main-search.liquid alongside component-card-colors.css.

   The strip is already correct with no JavaScript at all: real links to the colourway, crawlable, and a
   middle-click opens it. This file only replaces the navigation with an in-place swap, and turns a row of
   eight tab stops into one with arrow keys.

   What a swap touches, and only inside its own card: the visible photo, the outlined chip, every href that
   points at the product (name link, media link(s), the view-product bag link), data-url on the card and on
   sf-quick-add-button, and the size strip when its pills carry per-colour data. Every pick also repaints
   what the colourway itself decides: the price and compare-at, the sold-out flag, and the bag's sold-out
   state, from the verdicts snippets/price.liquid wrote onto the chip.
   The card is RENDERED in its outlined chip's state already (product-card.liquid builds price, flags, bag,
   links, photo and size strip from one initial variant), so none of this is needed to make first paint
   right, and a tap on the chip that is already outlined rewrites every one of those to what it already is.

   A chip tap is handled here, in the bubble phase on this element; page-transition.js decides on the WINDOW
   in the bubble phase, after this has called preventDefault, so it never turns a pick into a page change.

   What it deliberately does NOT touch: the favourite. theme.js keys favourites on the product handle
   (FAV_KEY entries are deduped on .handle), so a colour cannot move the saved target — it only changes the
   url and thumbnail that a later save records, which is the behaviour we want.

   No node is created or destroyed. The media stays the same scroll-snap container card-swipe.js is holding,
   so its scroll position, its snap points and its listeners all survive a colour change. */
'use strict';
(function () {
  const theme = window.theme || {};
  if (customElements.get('sf-card-colors')) return;

  const DRAG_SLOP = 10;   /* px of pointer travel that turns a tap on a chip into a drag of the strip */
  const EDGE = 1;         /* scrollLeft slack: sub-pixel layout never means "there is more over there" */
  /* --card-colors-fade in component-card-colors.css, plus the 2px focus ring and 2px of air. A chip
     revealed any closer to the edge has its ring painted through the gradient. */
  const FADE = 24;
  const CLEAR = FADE + 4;

  const reduced = () => !!(theme.reducedMotion || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches));

  customElements.define('sf-card-colors', class extends HTMLElement {
    /* facets.js replaces [data-facets-results] wholesale and appends pages of cards; both adopt nodes into
       the live document, so connectedCallback can run more than once on the same element when an ancestor
       is re-inserted. A live AbortController means we are already bound. */
    connectedCallback() {
      if (this._ctrl && !this._ctrl.signal.aborted) return;
      this._ctrl = new AbortController();
      const { signal } = this._ctrl;

      this.addEventListener('pointerdown', (e) => this._onDown(e), { passive: true, signal });
      /* the browser releases a touch it has taken over for scrolling with pointercancel / lostpointercapture
         and then sends no click at all, so the press has to be forgotten here too (see _onDown) */
      this.addEventListener('lostpointercapture', () => { this._from = null; }, { passive: true, signal });
      this.addEventListener('click', (e) => this._onClick(e), { signal });
      this.addEventListener('keydown', (e) => this._onKey(e), { signal });
      this.addEventListener('scroll', () => this._edges(), { passive: true, signal });
      window.addEventListener('resize', theme.debounce ? theme.debounce(() => this._edges(), 100) : () => this._edges(), { passive: true, signal });

      /* a custom element upgrades when its OPENING tag is parsed, so the chips may not exist yet */
      this._collect();
      if (!this.chips.length) requestAnimationFrame(() => { if (this.isConnected) this._collect(); });
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      if (this._press) this._press.abort();
    }

    /* The DOM is the record of what the shopper picked, so it is what we read back. connectedCallback runs
       again whenever an ancestor is re-parented, and taking the selection from data-initial there threw the
       shopper's choice away: the outline and aria-current stayed on the colour they picked while this
       object and the tab stop moved back to the rendered one, after which clicking the rendered colour did
       nothing at all (chip === this.selected, so _select skipped the swap) and a third pick left two chips
       outlined. data-initial is only the fallback it always was — the state the card was BUILT in, for a
       strip nobody has touched yet. */
    _collect() {
      this.card = this.closest('.card-b');
      this.chips = Array.from(this.querySelectorAll('.card-b__color'));
      if (!this.chips.length) return;
      this.selected = this.chips.find((c) => c.classList.contains('is-selected'))
        || this.chips.find((c) => c.hasAttribute('data-initial'))
        || this.chips[0];
      /* roving tabindex is applied here, not in Liquid: without JS every chip must stay reachable */
      this._mark(this.selected);
      /* Liquid already rendered the strip narrowed to the outlined colourway; repeating the filter is a
         no-op there, and puts a strip back in step with the pick after a re-parent */
      this._filterSizes(this.selected.dataset.colourHandle);
      /* The outlined chip is the first AVAILABLE colourway, which on a narrow card can sit past the strip's
         visible end — the shopper would see no chip outlined at all. Brought in instantly: this is where the
         strip starts, not a movement. Skipped for the first chip (the usual case), which is already in view,
         so most cards pay no layout read for it. */
      if (this.chips.indexOf(this.selected) > 0) this._reveal(this.selected, true);
      this._edges();
    }

    /* One chip outlined, one tab stop, both on the same chip — written as a sweep rather than as "clear the
       old one, set the new one" so that a strip which arrives with two chips marked (a re-render racing a
       pick) is corrected instead of carried forward. */
    _mark(chip) {
      this.chips.forEach((c) => {
        const on = c === chip;
        c.classList.toggle('is-selected', on);
        if (on) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current');
        c.setAttribute('tabindex', on ? '0' : '-1');
      });
    }

    /* A press is measured from pointerdown to pointerup and the verdict ("that was a drag") is what the
       click reads. The origin used to live until a click consumed it, and a press that ended without one —
       released off the chip, cancelled by a touch scroll of the strip — left it behind: the next keyboard
       Enter (clientX/Y 0) then measured as a long drag and the pick was swallowed. So the press is closed on
       pointerup and pointercancel, listened for on the WINDOW (a release off the strip never reaches this
       element) and only for the length of the press. */
    _onDown(e) {
      if (!e.isPrimary) return;
      if (this._press) this._press.abort();
      this._press = new AbortController();
      const opts = { passive: true, signal: this._press.signal };
      this._from = { x: e.clientX, y: e.clientY };
      this._dragged = false;
      const end = (ev) => {
        const from = this._from;
        this._dragged = !!(from && ev.type === 'pointerup' &&
          (Math.abs(ev.clientX - from.x) > DRAG_SLOP || Math.abs(ev.clientY - from.y) > DRAG_SLOP));
        this._from = null;
        if (this._press) { this._press.abort(); this._press = null; }
      };
      window.addEventListener('pointerup', end, opts);
      window.addEventListener('pointercancel', end, opts);
    }

    /* A horizontal drag of the strip must never open the product. The browser already suppresses click
       after a touch scroll; a mouse drag it does not, so the travel is measured either way. */
    _onClick(e) {
      const chip = e.target.closest ? e.target.closest('.card-b__color') : null;
      if (!chip || !this.contains(chip)) return;
      /* the point of using anchors: a modified click still opens the colourway in a tab or a window */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
      e.preventDefault();
      const dragged = this._dragged;
      this._dragged = false;
      /* detail 0 is a click no pointer made — Enter, Space, a screen reader's activate — and can never be
         a drag, whatever an earlier press left behind */
      if (e.detail !== 0 && dragged) return;
      this._select(chip, false);
    }

    _onKey(e) {
      if (!this.chips || this.chips.length < 2) return;
      const at = this.chips.indexOf(document.activeElement);
      if (at < 0) return;
      const forward = theme.rtl ? 'ArrowLeft' : 'ArrowRight';
      const back = theme.rtl ? 'ArrowRight' : 'ArrowLeft';
      let next = null;
      if (e.key === forward) next = (at + 1) % this.chips.length;
      else if (e.key === back) next = (at - 1 + this.chips.length) % this.chips.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = this.chips.length - 1;
      if (next === null) return;
      e.preventDefault();
      this._select(this.chips[next], true);
    }

    /* Only the photo and the event wait for a CHANGE of colour. Everything else runs on every pick, the
       outlined chip included, and every one of those writes is idempotent: the card was rendered in its
       outlined chip's state, so re-picking that chip rewrites links, sizes, price, flags and bag to exactly
       what they already are — and re-picking it after a round trip through another colour restores them.
       @param {HTMLAnchorElement} chip @param {boolean} focus */
    _select(chip, focus) {
      if (!chip || !this.card) return;
      const changed = chip !== this.selected;
      if (changed) {
        this._mark(chip);
        this.selected = chip;
        this._swapMedia(chip);
      }
      const href = chip.getAttribute('href');
      this.card.dataset.url = href;
      if (chip.dataset.image) this.card.dataset.image = chip.dataset.image;
      this.card.querySelectorAll('a.card-b__link, a.card-b__media, a.card-b__slide, a.card-act--bag')
        .forEach((a) => { a.href = href; });
      /* sf-quick-add-button carries its OWN data-url, which beats the card's in theme.js cardMeta() */
      this.card.querySelectorAll('sf-quick-add-button[data-url]').forEach((b) => { b.dataset.url = href; });
      this._filterSizes(chip.dataset.colourHandle);
      this._offer(chip);
      if (changed) {
        /* card-swipe.js listens for this on window: its hint writes scrollLeft on a rAF loop and none of
           its yield triggers fire for a tap on a chip */
        this.dispatchEvent(new CustomEvent('colors:change', {
          bubbles: true,
          detail: { colour: chip.dataset.colour, variant: chip.dataset.variant, url: href, card: this.card }
        }));
      }
      this._reveal(chip);
      if (focus) chip.focus({ preventScroll: true });
    }

    _offer(chip) {
      const d = chip.dataset;
      if (!('priceText' in d)) return;   /* markup older than the per-colour verdicts: leave the card as built */
      const available = d.available !== 'false';
      if (d.price) this.card.dataset.price = d.price;
      this._paintPrice(d, available);
      this._paintFlags(available);
      this._paintBag(available);
    }

    /* Rebuilt in snippets/price.liquid's own shape — sr-only label, amount, and on a sale the second label and
       the struck-through compare-at — so a price repainted here reads exactly like one rendered there. */
    _paintPrice(d, available) {
      const price = this.card.querySelector(':scope > .price');
      if (!price) return;
      const sale = !!d.compareText;
      price.classList.toggle('price--sale', sale);
      price.classList.toggle('price--sold-out', !available);
      price.classList.toggle('price--range', d.varies === 'true');
      const sr = (text) => {
        const s = document.createElement('span');
        s.className = 'sr-only';
        s.textContent = text || '';
        return s;
      };
      const was = price.querySelector('.price__amount');
      const amount = document.createElement('span');
      amount.className = was ? was.className : 'price__amount';
      amount.textContent = d.priceText;
      const nodes = [sr(sale ? this.dataset.labelSale : this.dataset.labelRegular), amount];
      if (sale) {
        const s = document.createElement('s');
        s.className = 'price__compare';
        s.textContent = d.compareText;
        nodes.push(sr(this.dataset.labelRegular), s);
      }
      price.replaceChildren(...nodes);
    }

    /* The flag row the card was rendered with belongs to its initial (data-initial) colourway: the product's
       NEW / N LEFT row when that colourway can be bought, the product's "Sold out" when it cannot. Any pick
       with the same availability gets that row back exactly — kept, not rebuilt — so re-picking the outlined
       chip changes nothing. A sold-out pick on an in-stock product says "Sold out"; an in-stock pick on a
       card rendered sold out (only possible if the data disagree) gets an empty, reserved row. */
    _paintFlags(available) {
      const flags = this.card.querySelector(':scope > .card-flags');
      if (!flags) return;
      if (!this._flagsHome) {
        const initial = this.chips.find((c) => c.hasAttribute('data-initial'));
        const kids = flags.querySelector('[data-flag="kids"]');
        this._flagsHome = {
          html: flags.innerHTML,
          hidden: flags.getAttribute('aria-hidden'),
          available: !initial || initial.dataset.available !== 'false',
          kids: kids ? kids.textContent : ''
        };
      }
      if (available === this._flagsHome.available) {
        flags.innerHTML = this._flagsHome.html;
        if (this._flagsHome.hidden === null) flags.removeAttribute('aria-hidden');
        else flags.setAttribute('aria-hidden', this._flagsHome.hidden);
        return;
      }
      /* the audience flag (KIDS) belongs to the product, not the colourway, so it stays on either row */
      const row = [];
      if (!available) {
        const flag = document.createElement('span');
        flag.className = 'card-flags__flag';
        flag.textContent = this.dataset.labelSoldOut || '';
        row.push(flag);
      }
      if (this._flagsHome.kids) {
        if (row.length) {
          const sep = document.createElement('span');
          sep.className = 'card-flags__sep';
          sep.setAttribute('aria-hidden', 'true');
          sep.textContent = '·';
          row.push(sep);
        }
        const kids = document.createElement('span');
        kids.className = 'card-flags__flag';
        kids.dataset.flag = 'kids';
        kids.textContent = this._flagsHome.kids;
        row.push(kids);
      }
      flags.replaceChildren(...row);
      if (row.length) flags.removeAttribute('aria-hidden');
      else flags.setAttribute('aria-hidden', 'true');
    }

    /* The bag opens quick-add either way (the drawer shows the colourway's sizes struck through and a disabled
       add); what follows availability is its muted tint and the " — Sold out" end of its name. */
    _paintBag(available) {
      const bag = this.card.querySelector('.card-acts .card-act--bag');
      if (!bag) return;
      bag.classList.toggle('card-act--soldout', !available);
      const label = bag.getAttribute('aria-label');
      if (label == null || !this.dataset.labelSoldOut) return;
      const suffix = ' — ' + this.dataset.labelSoldOut;
      const base = label.slice(-suffix.length) === suffix ? label.slice(0, -suffix.length) : label;
      bag.setAttribute('aria-label', available ? base : base + suffix);
    }

    /* Each chip carries ONE photo of its colourway (its variant's media); product.media carries no colour
       association at all. So a non-initial colour shows that one photo and the rest of the track is hidden —
       the alternative, leaving the initial colourway's second and third shots under a different colour's
       name, is a lie about the merchandise. Going back to the chip the card was rendered with restores the
       track from the cache, because that track is the one product-card.liquid built for that colourway. */
    _swapMedia(chip) {
      const track = this.card.querySelector('.card-b__track');
      const slides = track ? Array.from(track.querySelectorAll('.card-b__slide')) : [];
      const frame = slides[0] || this.card.querySelector('a.card-b__media');
      const lead = frame && frame.querySelector('img');
      if (!lead) return;   /* a card with no artwork yet shows .card-b__pending; nothing to swap */
      if (!this._home) this._home = { src: lead.getAttribute('src') || '', srcset: lead.getAttribute('srcset') || '', sizes: lead.getAttribute('sizes') || '' };

      if (chip.hasAttribute('data-initial')) {
        if (this._home.srcset) lead.setAttribute('srcset', this._home.srcset); else lead.removeAttribute('srcset');
        if (this._home.sizes) lead.setAttribute('sizes', this._home.sizes); else lead.removeAttribute('sizes');
        if (this._home.src) lead.setAttribute('src', this._home.src);
        slides.slice(1).forEach((s) => { s.hidden = false; });
        return;
      }
      if (!chip.dataset.image) return;
      /* srcset wins over src, so it has to go before the swap or nothing changes */
      lead.removeAttribute('srcset');
      lead.removeAttribute('sizes');
      lead.setAttribute('src', chip.dataset.image);
      slides.slice(1).forEach((s) => { s.hidden = true; });
      if (track) track.scrollLeft = 0;
    }

    /* The size strip is built server-side for the whole product. It can only be narrowed to one colourway
       once snippets/size-list.liquid tags each pill with data-colours — a comma-separated list of colour
       value HANDLES, the same token this card's chips carry as data-colour-handle. Until that attribute
       appears this is a no-op and the card keeps exactly the strip it has today.
       Split, never substring: "black" is a prefix of "core-black", and a comma-separated list gives an
       indexOf no delimiters to hide behind. data-colours-available is the availability twin, in the same
       format; without it no pill is called sold out, rather than every pill. The pill's aria-label follows
       the same verdict, because size-list writes it from the size-wide availability. */
    _filterSizes(handle) {
      const strip = this.card.querySelector('.card-sizes');
      if (!strip || !handle) return;
      const pills = Array.from(strip.querySelectorAll('.size-pill'));
      if (!pills.length || !pills.some((p) => p.dataset.colours)) return;
      const list = (v) => (v || '').split(',').map((s) => s.trim());
      let shown = 0;
      pills.forEach((pill) => {
        const has = list(pill.dataset.colours).indexOf(handle) > -1;
        pill.hidden = !has;
        /* theme.css draws the middots with .size-pill + .size-pill::before, which would leave a leading
           separator when the pills before the first visible one are hidden */
        pill.classList.toggle('is-first-shown', has && shown === 0);
        if (has) {
          shown += 1;
          if ('coloursAvailable' in pill.dataset) {
            const out = list(pill.dataset.coloursAvailable).indexOf(handle) === -1;
            pill.classList.toggle('is-sold-out', out);
            if (out && this.dataset.labelSoldOut) pill.setAttribute('aria-label', pill.textContent.trim() + ' — ' + this.dataset.labelSoldOut);
            else if (!out) pill.removeAttribute('aria-label');
          }
        }
      });
      let empty = strip.querySelector('.card-sizes__empty');
      if (!shown && !empty && strip.dataset.nosizes) {
        empty = document.createElement('span');
        empty.className = 'card-sizes__empty';
        empty.textContent = strip.dataset.nosizes;
        strip.appendChild(empty);
      }
      if (empty) empty.hidden = shown > 0;
      const unit = strip.querySelector('.card-sizes__unit');
      if (unit) unit.hidden = shown === 0;
    }

    /* Scroll only the strip, never the page: scrollIntoView would move the document too. Physical deltas
       work in both directions — scrollLeft is a physical-axis property whatever the origin is in RTL.

       Revealed clear of the edge fade, not merely inside the scrollport: an overflowing strip masks CLEAR
       px at whichever edge still has chips behind it, and a chip brought flush against that edge arrives
       under the gradient. Only while the strip is wide enough to hold a chip and both margins — on a
       narrower one the two branches would push each other back and forth — and only while it overflows,
       since with no overflow there is no mask and nowhere to scroll to. */
    /* @param {Element} chip @param {boolean} [instant] no glide (the strip's starting position on connect) */
    _reveal(chip, instant) {
      const sr = this.getBoundingClientRect();
      const cr = chip.getBoundingClientRect();
      const over = this.scrollWidth - this.clientWidth > EDGE;
      const pad = (over && sr.width - cr.width > CLEAR * 2) ? CLEAR : 2;
      let dx = 0;
      if (cr.left < sr.left + pad) dx = cr.left - sr.left - pad;
      else if (cr.right > sr.right - pad) dx = cr.right - sr.right + pad;
      if (!dx) return;
      this.scrollBy({ left: dx, behavior: (instant || reduced()) ? 'auto' : 'smooth' });
    }

    /* The fade that says there are more colours off the edge. Same class contract as .tabs-underline
       (has-overflow / at-start / at-end) but bound unconditionally and with Math.abs: SFTabs' own bindings
       sit in the `else` of an `if ('ResizeObserver' in window)`, so they never run in a current browser,
       and its `scrollLeft <= 1` test cannot work in RTL, where Chrome reports a negative scrollLeft. */
    _edges() {
      const max = this.scrollWidth - this.clientWidth;
      const on = max > EDGE;
      const x = Math.abs(this.scrollLeft);
      this.classList.toggle('has-overflow', on);
      this.classList.toggle('at-start', !on || x <= EDGE);
      this.classList.toggle('at-end', !on || x >= max - EDGE);
    }
  });
})();
